import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import hourglassIcon from '../assets/time glass sand.png'; // purple hourglass
import honestyIcon from '../assets/code and measurement.png'; // green honesty icon
import neutralIcon from '../assets/balanced.png'; // red neutral icon
import bgCircle from '../assets/rectangle shape.png';
import bgTriangle from '../assets/triangle.png';
import bgStar from '../assets/star frame.png';
import bgHeart from '../assets/heart frame.png';
import msgFrame from '../assets/msg frame.png';
import phoneFrame from '../assets/phone frame.png';
// Header handled by shared Navbar
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar'
import DiSCResults from '../components/DiSCResults'
import Footer from './Footer';
import { toast } from 'react-toastify';
import api from "../sever/Api";
import { notifyTestCompletion } from '../lib/testUtils';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../components/card";
import { Button } from "../components/button";
import { Brain, ArrowRight } from "lucide-react";

const questions = [
  // 1. Dominance (D) – Focus on control, power, results
  {
    question: 'When working on a project, I usually prefer to:',
    options: [
      { label: 'Take charge and lead the group' },
      { label: 'Encourage everyone to participate' },
      { label: 'Support the team quietly' },
      { label: 'Focus on details and accuracy' },
    ],
  },
  {
    question: 'If there is a conflict in my team, I usually:',
    options: [
      { label: 'Confront it directly to resolve quickly' },
      { label: 'Try to lighten the mood with humor' },
      { label: 'Stay calm and mediate' },
      { label: 'Analyze the facts before speaking' },
    ],
  },
  {
    question: 'I am most motivated by:',
    options: [
      { label: 'Winning and achieving results' },
      { label: 'Recognition and approval' },
      { label: 'Harmony and stability' },
      { label: 'Accuracy and correctness' },
    ],
  },
  {
    question: 'People usually describe me as:',
    options: [
      { label: 'Assertive and determined' },
      { label: 'Outgoing and optimistic' },
      { label: 'Loyal and dependable' },
      { label: 'Logical and cautious' },
    ],
  },
  {
    question: 'In a decision-making situation, I prefer:',
    options: [
      { label: 'Making quick, bold decisions' },
      { label: 'Getting input from others' },
      { label: 'Taking time to ensure everyone agrees' },
      { label: 'Researching deeply before deciding' },
    ],
  },
  // 2. Influence (I) – Focus on relationships, communication, persuasion
  {
    question: 'In a group, I usually:',
    options: [
      { label: 'Push for progress and results' },
      { label: 'Motivate others with enthusiasm' },
      { label: 'Keep peace and cooperation' },
      { label: 'Check for mistakes and accuracy' },
    ],
  },
  {
    question: 'I feel happiest when:',
    options: [
      { label: 'I complete big goals' },
      { label: 'I am surrounded by people and fun' },
      { label: 'I am in a stable environment' },
      { label: 'I deliver quality and precision' },
    ],
  },
  {
    question: 'People often see me as:',
    options: [
      { label: 'Confident and forceful' },
      { label: 'Friendly and talkative' },
      { label: 'Supportive and caring' },
      { label: 'Careful and analytical' },
    ],
  },
  {
    question: 'My communication style is:',
    options: [
      { label: 'Direct and to the point' },
      { label: 'Warm and energetic' },
      { label: 'Patient and understanding' },
      { label: 'Formal and detailed' },
    ],
  },
  {
    question: 'I am most likely to:',
    options: [
      { label: 'Compete to win' },
      { label: 'Inspire others with positivity' },
      { label: 'Help teammates feel secure' },
      { label: 'Organize and plan everything carefully' },
    ],
  },
  // 3. Steadiness (S) – Focus on support, stability, cooperation
  {
    question: 'When working in a team, I prefer to:',
    options: [
      { label: 'Lead the group with authority' },
      { label: 'Share ideas openly and encourage fun' },
      { label: 'Support others and maintain harmony' },
      { label: 'Focus on rules and details' },
    ],
  },
  {
    question: 'In stressful situations, I usually:',
    options: [
      { label: 'Push harder to get things done' },
      { label: 'Seek encouragement from others' },
      { label: 'Stay calm and steady' },
      { label: 'Review everything carefully before acting' },
    ],
  },
  {
    question: 'People often describe me as:',
    options: [
      { label: 'Strong-willed and ambitious' },
      { label: 'Energetic and optimistic' },
      { label: 'Gentle and dependable' },
      { label: 'Serious and precise' },
    ],
  },
  {
    question: 'I value most:',
    options: [
      { label: 'Achievement and results' },
      { label: 'Recognition and influence' },
      { label: 'Security and cooperation' },
      { label: 'Accuracy and logic' },
    ],
  },
  {
    question: 'My strength is:',
    options: [
      { label: 'Driving progress quickly' },
      { label: 'Inspiring and influencing others' },
      { label: 'Remaining patient and loyal' },
      { label: 'Analyzing situations logically' },
    ],
  },
  // 4. Conscientiousness (C) – Focus on accuracy, rules, quality
  {
    question: 'I make decisions by:',
    options: [
      { label: 'Acting fast and boldly' },
      { label: 'Asking others\' opinions' },
      { label: 'Taking time for group harmony' },
      { label: 'Carefully analyzing all facts' },
    ],
  },
  {
    question: 'In group projects, I usually:',
    options: [
      { label: 'Take control to push things forward' },
      { label: 'Motivate with enthusiasm' },
      { label: 'Support the team quietly' },
      { label: 'Ensure rules and quality are followed' },
    ],
  },
  {
    question: 'People often say I am:',
    options: [
      { label: 'Bold and decisive' },
      { label: 'Outgoing and charming' },
      { label: 'Calm and friendly' },
      { label: 'Logical and perfectionistic' },
    ],
  },
  {
    question: 'I feel most comfortable when:',
    options: [
      { label: 'I\'m making progress quickly' },
      { label: 'I\'m connecting with people' },
      { label: 'I\'m in a supportive environment' },
      { label: 'I\'m following clear rules and structure' },
    ],
  },
  {
    question: 'My main focus is:',
    options: [
      { label: 'Winning and control' },
      { label: 'Relationships and fun' },
      { label: 'Stability and loyalty' },
      { label: 'Accuracy and quality' },
    ],
  },
  // 5. Mixed / Situational (all DiSC blended)
  {
    question: 'If my boss gives me urgent work, I will:',
    options: [
      { label: 'Finish it fast, no excuses' },
      { label: 'Ask others to join and make it fun' },
      { label: 'Stay calm and work consistently' },
      { label: 'Double-check details before submitting' },
    ],
  },
  {
    question: 'In a meeting, I usually:',
    options: [
      { label: 'Push my ideas strongly' },
      { label: 'Talk a lot and keep it lively' },
      { label: 'Listen and support others' },
      { label: 'Present facts with data' },
    ],
  },
  {
    question: 'If my team misses a deadline, I:',
    options: [
      { label: 'Push harder and demand results' },
      { label: 'Try to cheer them up' },
      { label: 'Stay supportive and avoid blame' },
      { label: 'Review carefully where mistakes happened' },
    ],
  },
  {
    question: 'My biggest weakness might be:',
    options: [
      { label: 'Being too aggressive' },
      { label: 'Talking too much' },
      { label: 'Avoiding confrontation' },
      { label: 'Overthinking and being critical' },
    ],
  },
  {
    question: 'If I join a new workplace, I first try to:',
    options: [
      { label: 'Show I can take charge' },
      { label: 'Make new friends quickly' },
      { label: 'Build trust and stability' },
      { label: 'Learn all rules and systems' },
    ],
  },
];

const DiScTest = () => {
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [selected, setSelected] = useState(null);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  const [previousResults, setPreviousResults] = useState([]);
  const [showPreviousResults, setShowPreviousResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const currentQuestion = questions[currentStep];
  const totalSteps = questions.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  // Restore selection when moving between questions
  useEffect(() => {
    if (Object.prototype.hasOwnProperty.call(answers, currentStep)) {
      setSelected(answers[currentStep]);
    } else {
      setSelected(null);
    }
  }, [currentStep, answers]);

  // Fetch previous DiSC results
  const fetchPreviousResults = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      setLoading(true);

      const { data } = await api.get("/api/results/disc");
      setPreviousResults(data.results || []);

    } catch (error) {
      console.error("Error fetching previous results:", error);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchPreviousResults();
  }, []);



  // Check for showResults URL parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const showResultsParam = params.get('showResults');
    if (showResultsParam === 'true') {
      setShowPreviousResults(true);
    }
  }, [location.search]);

const handleNext = (selectedIdx) => {
  if (selectedIdx === null || selectedIdx === undefined) return;

  setAnswers(prev => ({
    ...prev,
    [currentStep]: selectedIdx
  }));

  if (currentStep < totalSteps - 1) {
    setCurrentStep(prev => prev + 1);
    setSelected(null);
  } else {
     const finalAnswers = {
      ...answers,
      [currentStep]: selectedIdx
    };
    const calculatedResults = calculateResults(finalAnswers);
    setSaving(true);
    saveTestResult(finalAnswers, calculatedResults)
      .finally(() => setSaving(false));
  }
};

  const calculateResults = (finalAnswers) => {
    // DiSC scoring system - each option corresponds to D, I, S, C respectively
    let dScore = 0; // Dominance
    let iScore = 0; // Influence  
    let sScore = 0; // Steadiness
    let cScore = 0; // Conscientiousness

    Object.values(finalAnswers).forEach((answer) => {
      switch (answer) {
        case 0: // First option - D
          dScore += 1;
          break;
        case 1: // Second option - I
          iScore += 1;
          break;
        case 2: // Third option - S
          sScore += 1;
          break;
        case 3: // Fourth option - C
          cScore += 1;
          break;
        default:
          break;
      }
    });

    // Calculate percentages for each dimension
    const totalQuestions = questions.length;
    const dPercentage = Math.round((dScore / totalQuestions) * 100);
    const iPercentage = Math.round((iScore / totalQuestions) * 100);
    const sPercentage = Math.round((sScore / totalQuestions) * 100);
    const cPercentage = Math.round((cScore / totalQuestions) * 100);

    // Determine primary personality type
    const scores = [dScore, iScore, sScore, cScore];
    const maxScore = Math.max(...scores);
    let personalityType = '';

    if (dScore === maxScore) {
      personalityType = 'Dominant';
    } else if (iScore === maxScore) {
      personalityType = 'Influential';
    } else if (sScore === maxScore) {
      personalityType = 'Steady';
    } else {
      personalityType = 'Conscientious';
    }

    // Calculate overall score (average of all dimensions)
    const overallScore = dScore + iScore + sScore + cScore;
    const overallPercentage = Math.round((overallScore / (totalQuestions * 4)) * 100);

    return {
      scores: overallScore,
      maxPossibleScore: totalQuestions * 4,
      percentage: overallPercentage,
      personalityType,
      discScores: {
        d: dScore,
        i: iScore,
        s: sScore,
        c: cScore,
        dPercentage,
        iPercentage,
        sPercentage,
        cPercentage
      },
      answers: finalAnswers
    };
  };

  const buildDetailedAnswers = (finalAnswers) => {
    const detailedAnswers = [];
    questions.forEach((question, index) => {
      const answer = finalAnswers[index];
      if (answer !== undefined) {
        detailedAnswers.push({
          questionId: index.toString(),
          questionText: question.question,
          selectedOptionId: answer.toString(),
          selectedOptionText: question.options[answer].label
        });
      }
    });
    return detailedAnswers;
  };

  const saveTestResult = async (finalAnswersParam, localResultsParam) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Please login to save your results.");
        return;
      }

      const detailedAnswers = buildDetailedAnswers(finalAnswersParam);

      const { data: discData } = await api.post("/api/results/disc", {
        answers: detailedAnswers,
        score: {
          overallScore: localResultsParam.scores,
          personalityType: localResultsParam.personalityType,
          percentage: localResultsParam.percentage,
          discScores: localResultsParam.discScores,
        },
      });

      if (discData && discData.result) {
        setResults(discData.result);
      }
      setShowResults(true);
      toast.success("DiSC results saved successfully!");

      // Notify test completion for real-time updates
      const testData = {
        scores: {
          overallScore: localResultsParam.scores,
          personalityType: localResultsParam.personalityType,
          discScores: localResultsParam.discScores,
        },
        answers: detailedAnswers,
        overallScore: localResultsParam.scores,
        overallPercentage: localResultsParam.percentage,
        testType: 'disc',
        completed: true
      };

      notifyTestCompletion('disc', testData);

      try {
        await api.post("/api/test-results", {
          testType: "disc",
          answers: detailedAnswers,
          scores: {
            overallScore: localResultsParam.scores,
            personalityType: localResultsParam.personalityType,
            discScores: localResultsParam.discScores,
          },
          overallScore: localResultsParam.scores,
          overallPercentage: localResultsParam.percentage,
        });
      } catch (err) {
        console.error(
          "Failed to save to general test results:",
          err.response?.data?.message
        );
      }
    } catch (error) {
      console.error("Error saving test result:", error);
      toast.error(error.response?.data?.message || "Failed to save test results");
    }
  };

  // Removed auto-save-on-show; we now save before showing results

  const resetTest = () => {
    setCurrentStep(0);
    setSelected(null);
    setAnswers({});
    setShowResults(false);
    setResults(null);
    setShowIntro(true);
  };

  if (showIntro) {
    return (
      <div className="min-h-screen w-full overflow-x-hidden bg-gradient-to-br from-[#EFF6FF] via-white to-[#F9FAFB] relative">
        {/* Sidebar */}
        <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
          <Sidebar />
        </div>

        {/* Main content */}
        <div className="ml-64">
          <Navbar />

          <div className="w-full max-w-7xl mx-auto pt-10 pb-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl mx-auto">
              <Card className="text-center bg-white shadow-lg" >
                <CardHeader className="space-y-4">

                  <CardTitle className="text-3xl font-bold ">DISC Assessment</CardTitle>
                  <CardDescription className="text-lg text-gray-600">
                    Discover your behavior style & communication preferences
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                    <div className="space-y-2">
                      <h3 className="font-semibold text-red-600">Dominance (D)</h3>
                      <p className="text-sm text-gray-600">Control, power & results</p>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold text-yellow-600">Influence (I)</h3>
                      <p className="text-sm text-gray-600">Relationships & persuasion</p>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold text-green-600">Steadiness (S)</h3>
                      <p className="text-sm text-gray-600">Stability & cooperation</p>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold text-blue-600">Conscientiousness (C)</h3>
                      <p className="text-sm text-gray-600">Accuracy & quality</p>
                    </div>
                  </div>

                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-sm text-purple-800">
                      <strong>{totalSteps} questions</strong>  • Gain insights into your style
                    </p>
                  </div>

                  <Button
                    onClick={() => {
                      // Mark free test as used when user actually starts the test
                      try {
                        const userStr = localStorage.getItem('user');
                        const user = userStr ? JSON.parse(userStr) : null;
                        const isPremium = user && (user.role === 'premium' || user.role === 'admin');
                        if (!isPremium) {
                          localStorage.setItem('freeTestUsed', '1');
                        }
                      } catch (_) {}
                      setShowIntro(false);
                    }}
                    className="w-full text-lg py-6 bg-purple-600 hover:bg-purple-700"
                    size="lg"
                  >
                    Start Test <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }



  // Show results if test is completed
  if (showResults && results) {
    return (
      <div className="min-h-screen w-full overflow-x-hidden bg-gradient-to-br from-[#EFF6FF] via-white to-[#F9FAFB] relative">
        <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
          <Sidebar />
        </div>
        <div className="ml-64">
          <Navbar toggleSidebar={() => { }} />

          <div className="w-full max-w-7xl mx-auto pt-10 pb-8 px-4 sm:px-6 lg:px-8">
            <DiSCResults results={results} onRetakeTest={resetTest} />
          </div>
        </div>
      </div>
    );
  }



  // Show previous results view
  if (showPreviousResults) {
    return (
      <div className="min-h-screen w-full overflow-x-hidden bg-gradient-to-br from-[#EFF6FF] via-white to-[#F9FAFB] relative">
        <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
          <Sidebar />
        </div>
        <div className="ml-64">
          <Navbar toggleSidebar={() => { }} />

          <div className="w-full max-w-7xl mx-auto pt-10 pb-8 px-4 sm:px-6 lg:px-8">
            <div className="mx-auto bg-white rounded-xl shadow-lg p-6 max-w-4xl">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Previous DiSC Results</h2>
                <button
                  onClick={() => setShowPreviousResults(false)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Take New Test
                </button>
              </div>

              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading results...</p>
                </div>
              ) : previousResults.length > 0 ? (
                <div className="space-y-4">
                  {previousResults.map((result, index) => (
                    <div key={result._id || index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold text-gray-800">
                            Test #{previousResults.length - index} - {result.score?.dominantType || 'N/A'}
                          </h3>
                          <p className="text-sm text-gray-600">
                            Completed on {new Date(result.takenAt).toLocaleDateString()}
                          </p>
                          {result.score && (
                            <div className="mt-2 flex gap-4 text-sm">
                              <span className="text-red-600">D: {result.score.d?.percentage || 0}%</span>
                              <span className="text-yellow-600">I: {result.score.i?.percentage || 0}%</span>
                              <span className="text-green-600">S: {result.score.s?.percentage || 0}%</span>
                              <span className="text-blue-600">C: {result.score.c?.percentage || 0}%</span>
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => {
                            setResults(result);
                            setShowResults(true);
                            setShowPreviousResults(false);
                          }}
                          className="bg-gray-100 text-gray-700 px-3 py-1 rounded text-sm hover:bg-gray-200 transition-colors"
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600 mb-4">No previous results found.</p>
                  <button
                    onClick={() => setShowPreviousResults(false)}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Take Your First Test
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-gradient-to-br from-[#EFF6FF] via-white to-[#F9FAFB] relative">
      {/* <Navbar /> */}
      <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
        <Sidebar />
      </div>
      <div className="ml-64 ">
        <Navbar toggleSidebar={() => { }} />
        <img src={bgCircle} alt="bg" className="absolute left-4 sm:left-10 top-32 w-16 sm:w-32 opacity-10 hidden sm:block" />
        <img src={bgTriangle} alt="bg" className="absolute right-4 sm:right-10 top-24 w-6 sm:w-10 opacity-10 hidden sm:block" />
        <img src={bgStar} alt="bg" className="absolute left-1/2 top-10 w-6 sm:w-8 opacity-10 hidden sm:block" />
        <img src={bgHeart} alt="bg" className="absolute right-8 sm:right-20 top-1/2 w-12 sm:w-20 opacity-10 hidden sm:block" />

        {/* Main Content */}
        <div className="w-full max-w-7xl mx-auto pt-10 pb-8 px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold">DISC Assessment</h1>
          </div>
          <p className="text-center text-gray-600 mb-6 sm:mb-8 max-w-2xl mx-auto text-sm sm:text-base"></p>

          <div className="flex flex-col sm:flex-row items-start justify-between mb-8 sm:mb-10 mx-auto max-w-4xl gap-4 sm:gap-6">
            <div className="flex flex-col items-center text-center flex-1 min-h-[120px] sm:min-h-[155px]">
              <div className="flex items-center justify-center h-12 w-12 sm:h-16 sm:w-16">
                <img src={hourglassIcon} alt="hourglass" className="w-8 h-10 sm:w-11 sm:h-14" style={{ objectFit: 'contain' }} />
              </div>
              <span className="text-xs sm:text-sm text-gray-700 font-semibold mt-2">It takes very little of your time, so easy.</span>
            </div>
            <div className="flex flex-col items-center text-center flex-1 min-h-[120px] sm:min-h-[155px]">
              <div className="flex items-center justify-center h-12 w-12 sm:h-16 sm:w-16">
                <img src={honestyIcon} alt="honesty" className="w-10 h-10 sm:w-15 sm:h-15" style={{ objectFit: 'contain' }} />
              </div>
              <span className="text-xs sm:text-sm text-gray-700 font-semibold mt-2">Please be honest in your answer.<br />(Even if you don't like...)</span>
            </div>
            <div className="flex flex-col items-center text-center flex-1 min-h-[120px] sm:min-h-[155px]">
              <div className="flex items-center justify-center h-12 w-12 sm:h-16 sm:w-16">
                <img src={neutralIcon} alt="neutral" className="w-10 h-10 sm:w-16 sm:h-16" style={{ objectFit: 'contain' }} />
              </div>
              <span className="text-xs sm:text-sm text-gray-700 font-semibold mt-2">Try not to leave a "neutral" response.</span>
            </div>
          </div>
          <div style={{ width: '85%', margin: '0 auto' }}>
            {/* Question header */}
            <div className="flex justify-between items-center text-sm text-gray-600 mb-2">
              <span>Question {currentStep + 1} of {totalSteps}</span>
              <span>{Math.round(progress)}% Complete</span>
            </div>

            {/* Progress bar */}
            <div className="w-full h-1.5 bg-gray-200 rounded mb-6 overflow-hidden">
              <div
                className="h-full bg-purple-500 transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
          <div className="rounded-lg border text-card-foreground shadow-sm mx-auto bg-[#ffffff] " style={{ width: '85%' }}>



            {/* Question text */}
            <div className='p-5' style={{ borderBottom: '1px solid #ccc' }}>
              <h2 className="text-lg font-bold mb-1">{currentQuestion.question}</h2>
              <p className="text-sm text-gray-500 ">
                Curiosity, imagination, preference for novelty
              </p>
            </div>

            {/* Options */}
            <div className="space-y-3 mb-8 p-5">
              {currentQuestion.options.map((opt, idx) => (
                <label
                  key={opt.label}
                  className={`block border rounded-lg px-4 py-3 cursor-pointer text-base transition 
                ${selected === idx ? 'border-primary ring-2 ring-primary/50 bg-primary/5 dark:bg-primary/10' : 'hover:bg-[rgb(197,199,203)] dark:hover:bg-gray-800'}`}
                >
                  <input
                    type="radio"
                    name="answer"
                    checked={selected === idx}
                    onChange={() => {
                      setSelected(idx);
                      handleNext(idx);
                    }}
                    className="hidden"
                  />
                  {opt.label}
                </label>
              ))}
            </div>

            {/* Navigation buttons */}
          </div>
          <div className="flex justify-between gap-4 mt-5 mx-auto" style={{ width: '85%' }}>
            <button
              onClick={() => setCurrentStep(prev => Math.max(prev - 1, 0))}
              disabled={currentStep === 0}
              className="px-6 py-2 rounded border border-gray-300 text-gray-700 bg-white 
                 hover:bg-gray-100 disabled:opacity-50"
            >
              Previous
            </button>
            {/* <button
              onClick={handleNext}
              disabled={selected === null}
              className="px-6 py-2 rounded bg-purple-500 text-white hover:bg-purple-600 
                 disabled:opacity-50"
            >
              {currentStep === totalSteps - 1 ? 'Finish' : 'Next Question'}
            </button> */}
          </div>


        </div>
      </div>
      {/* <div style={{ height: '2px', width: '100%', background: ' linear-gradient(90deg, #ff5e62 0%, #ff9966 30%, #f9d423 50%, #a1c4fd 70%, #2b86c5 100%)' }} /> */}

      {/* <Footer /> */}
    </div>
  );
};

export default DiScTest; 